import {
  computed,
  ref,
  watch,
  type Ref,
} from 'vue'
import { format } from 'date-fns'
import getDateFnsLocaleByIso from 'src/functions/getDateFnsLocaleByIso'
import getDateItemsFromSlots from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuScheduleCalendar/functions/getDateItemsFromSlots'
import { DAYS_FOR_DOCTOR_SLOTS } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsSchedule/constants'
import type { CommonContextStore } from 'src/types/context'
import useContextStore from 'src/store/useContextStore'
import type { AppointmentPlaceType, LpuDoctor } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/types'
import type { DoctorSlotsByDate } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuScheduleCalendar/types'
import apiDoctorTelemedSlots from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/api/apiDoctorTelemedSlots'
import getTelemedScheduleType from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/functions/getTelemedScheduleType'
import mapTelemedSlots from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/functions/mapTelemedSlots'
import hasFreeSlots from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/functions/hasFreeSlots'

type UseDoctorCardScheduleOptions = {
  doctor: LpuDoctor
  lpuId: number
  clinicSlots: Ref<DoctorSlotsByDate>
  placeType: Ref<AppointmentPlaceType>
}

/**
 * Календарь карточки: слоты клиники с родителя, телемед — отдельный запрос.
 */
export default function useDoctorCardSchedule({
  doctor,
  lpuId,
  clinicSlots,
  placeType,
}: UseDoctorCardScheduleOptions) {
  const { context }: CommonContextStore = useContextStore()
  const telemedSlots = ref<DoctorSlotsByDate>({})
  const activeDate = ref<string | null>(null)
  /**
   * Заглушка «запись недоступна» только после ответа.
   * До него — скелетон, даже если watch ещё не стартовал запрос.
   */
  const isTelemedSlotsReady = ref(false)
  let telemedRequestId = 0

  const currentSlots = computed(() => (
    placeType.value === 'telemed' ? telemedSlots.value : clinicSlots.value
  ))
  const dayItems = computed(() => getDateItemsFromSlots(
    currentSlots.value,
    getDateFnsLocaleByIso(context.current_language.iso),
  ))
  const hasBookableSlots = computed(() => hasFreeSlots(currentSlots.value))
  const activeDaySlots = computed(() => (
    activeDate.value ? (currentSlots.value[activeDate.value] || []) : []
  ))
  const isTelemedLoading = computed(() => (
    placeType.value === 'telemed' && !isTelemedSlotsReady.value
  ))

  function selectFirstActiveDay() {
    activeDate.value = dayItems.value.find(item => !item.disabled)?.date || null
  }

  function selectDay(date: string) {
    activeDate.value = date
  }

  async function loadTelemedSlots() {
    if (isTelemedSlotsReady.value) {
      selectFirstActiveDay()

      return
    }

    if (!process.env.IS_CLIENT) {
      return
    }

    telemedRequestId += 1

    const requestId = telemedRequestId

    try {
      const { data } = await apiDoctorTelemedSlots.post({
        user_start_date: format(new Date(), 'yyyy-MM-dd'),
        user_timezone: Math.floor(new Date().getTimezoneOffset() / -60),
        days: DAYS_FOR_DOCTOR_SLOTS,
        only_free: true,
        lpu_params: [{
          lpu_id: lpuId,
          schedule_params: [{
            doctor_id: doctor.id,
            type_schedule: getTelemedScheduleType({
              telemed: doctor.min_prices?.tab_min_price_telemed,
              telemedMedtochka: doctor.min_prices?.tab_min_price_telemed_medtochka,
            }),
          }],
        }],
      })

      if (requestId !== telemedRequestId) {
        return
      }

      const response = Array.isArray(data) ? data : []
      const lpuSchedule = response.find(item => item.lpu_id === lpuId) || response[0]
      const doctorSchedule = lpuSchedule?.schedule_data.find(item => (
        Number(item.doctor_id) === Number(doctor.id)
      )) || lpuSchedule?.schedule_data[0]

      // eslint-disable-next-line require-atomic-updates
      telemedSlots.value = mapTelemedSlots(doctorSchedule?.schedule, doctor.id)
      selectFirstActiveDay()
    } catch {
      if (requestId === telemedRequestId) {
        // eslint-disable-next-line require-atomic-updates
        telemedSlots.value = {}
        activeDate.value = null
      }
    } finally {
      if (requestId === telemedRequestId) {
        // eslint-disable-next-line require-atomic-updates
        isTelemedSlotsReady.value = true
      }
    }
  }

  watch(clinicSlots, () => {
    if (placeType.value === 'clinic') {
      selectFirstActiveDay()
    }
  }, { immediate: true, deep: true })

  watch(placeType, nextType => {
    if (nextType === 'telemed') {
      loadTelemedSlots()

      return
    }

    if (nextType === 'clinic') {
      selectFirstActiveDay()
    }
  }, { immediate: true })

  return {
    dayItems,
    activeDate,
    activeDaySlots,
    hasBookableSlots,
    isTelemedLoading,
    selectDay,
  }
}
